"use client"

import { RefObject, useEffect, useRef } from "react"

/**
 * 适配顶部动态定位组件的滚动穿透 Hooks
 * 当 isShow 为 true 时，给 body 添加 overflow: hidden 防止页面滚动
 * @param {boolean} isShow - 蒙层显示/隐藏状态
 * @returns {React.RefObject<HTMLDivElement | null>} 绑定到蒙层容器的 ref（默认 HTMLDivElement，可根据实际容器类型调整）
 */
const usePreventScrollPenetration = (isShow: boolean): RefObject<HTMLDivElement | null> => {
  const maskRef = useRef<HTMLDivElement>(null)
  const originalOverflowRef = useRef<string>("")
  const hasSavedOriginalRef = useRef<boolean>(false)

  // 记录触摸开始位置，用于判断滚动方向
  const touchStartY = useRef<number>(0)
  const touchStartScrollTop = useRef<number>(0)

  useEffect(() => {
    // 在 effect 内部保存 ref 的引用，避免 cleanup 时 ref 已改变
    const maskEl = maskRef.current

    // 明确事件处理函数的事件类型为 TouchEvent
    const handleTouchStart = (e: TouchEvent): void => {
      if (!maskEl || !e.touches[0]) return
      touchStartY.current = e.touches[0].clientY
      touchStartScrollTop.current = maskEl.scrollTop
    }

    const handleTouchMove = (e: TouchEvent): void => {
      if (!maskEl || !e.touches[0]) return

      // 蒙层内容未超出，直接阻止滚动（防穿透）
      if (maskEl.scrollHeight <= maskEl.clientHeight) {
        e.preventDefault()
        return
      }

      // 计算滚动方向
      const currentY = e.touches[0].clientY
      const deltaY = currentY - touchStartY.current

      // 蒙层滚到顶/底时，阻止继续滚动（避免触发下层）
      const isTop = maskEl.scrollTop === 0
      const isBottom = Math.abs(maskEl.scrollTop + maskEl.clientHeight - maskEl.scrollHeight) < 1

      // 向上滚动（deltaY < 0）且已在顶部，或向下滚动（deltaY > 0）且已在底部时阻止
      if ((isTop && deltaY > 0) || (isBottom && deltaY < 0)) {
        e.preventDefault()
      }
      // 其他情况允许滚动，不阻止事件
    }

    if (typeof document !== "undefined") {
      // 只在第一次设置时保存原始 overflow 值
      if (!hasSavedOriginalRef.current) {
        originalOverflowRef.current = document.body.style.overflow
        hasSavedOriginalRef.current = true
      }

      if (isShow) {
        // 给 body 添加 overflow: hidden，防止页面滚动
        document.body.style.overflow = "hidden"
      } else {
        // 当 isShow 为 false 时，恢复 body 的 overflow 样式
        document.body.style.overflow = originalOverflowRef.current
      }
    }

    // 给蒙层绑事件
    if (isShow && maskEl) {
      maskEl.addEventListener("touchstart", handleTouchStart, { passive: true })
      maskEl.addEventListener("touchmove", handleTouchMove, { passive: false })
    }

    return () => {
      // 组件卸载时恢复 body 的 overflow 样式
      if (typeof document !== "undefined") {
        document.body.style.overflow = originalOverflowRef.current
      }

      // 使用保存的引用进行清理
      if (maskEl) {
        maskEl.removeEventListener("touchstart", handleTouchStart)
        maskEl.removeEventListener("touchmove", handleTouchMove)
      }
    }
  }, [isShow])

  return maskRef
}

export default usePreventScrollPenetration
